import { useEffect, useRef } from 'react';
import { tg, syncTelegramSheetBackground } from '../lib/telegram';
import { isKeyboardEditor, revealKeyboardEditor } from '../lib/keyboard-geometry';
import './sheet-interaction.css';
import { sheetViewportMotion } from './sheetViewportMotion';

export type DismissReason = 'escape' | 'backdrop' | 'handle' | 'swipe' | 'cancel';
type Layer = { element: HTMLElement; modal: boolean };
const layers: Layer[] = [];
const inertBefore = new Map<HTMLElement, boolean>();
let overflowBefore = '';
let locked = false;
function syncLayers() {
  // The existing layer stack owns backing color too: centered dialogs do not
  // release an underlying sheet, and no DOM observer races theme updates.
  syncTelegramSheetBackground(layers.some(layer => layer.element.matches('.sheet-interactive')));
  for (const [element, before] of inertBefore) element.inert = before;
  inertBefore.clear();
  const top = [...layers].reverse().find(layer => layer.modal);
  if (top) {
    if (!locked) { overflowBefore = document.body.style.overflow; locked = true; }
    document.body.style.overflow = 'hidden';
    // Inert siblings at every level, not the ancestor containing the portal.
    for (let node: HTMLElement | null = top.element; node && node !== document.body; node = node.parentElement) {
      for (const sibling of node.parentElement?.children ?? []) {
        if (sibling !== node && sibling instanceof HTMLElement) { inertBefore.set(sibling, sibling.inert); sibling.inert = true; }
      }
    }
  } else if (locked) { document.body.style.overflow = overflowBefore; locked = false; }
}
const editor = (element: Element | null) => Boolean(element?.matches('input,textarea,select,[contenteditable]:not([contenteditable="false"])'));
function focusables(element: HTMLElement) {
  return [...element.querySelectorAll<HTMLElement>('button,input,select,textarea,summary,a[href],[tabindex]')].filter(e => {
    if (e.tabIndex < 0 || e.matches(':disabled') || e.closest('[hidden],[inert]')) return false;
    for (let a = e.parentElement; a && a !== element; a = a.parentElement) {
      if (a instanceof HTMLDetailsElement && !a.open) {
        const summary = [...a.children].find(child => child.tagName === 'SUMMARY');
        if (!summary?.contains(e)) return false;
      }
    }
    return e.getClientRects().length > 0 && getComputedStyle(e).visibility === 'visible'
      && (typeof e.checkVisibility !== 'function' || e.checkVisibility());
  });
}

/** Modal ownership and touch ownership are independent: the camera panel is persistent. */
export function useSheet(onClose: (reason: DismissReason) => void, { busy = false, modal = true, swipe = true, returnFocus }: { busy?: boolean; modal?: boolean; swipe?: boolean; returnFocus?: () => void } = {}) {
  const panel = useRef<HTMLDivElement>(null);
  const current = useRef({ onClose, busy, returnFocus }); current.current = { onClose, busy, returnFocus };
  const request = useRef<(reason: DismissReason) => void>(() => {});
  useEffect(() => {
    const element = panel.current!;
    const prior = document.activeElement as HTMLElement | null;
    const layer = { element, modal }; layers.push(layer); syncLayers();
    // Startup may release its inert ownership after an initial modal mounted.
    // Refresh the remembered root state before restoring our modal ownership.
    const startupReady = () => {
      const root = document.getElementById('root');
      if (root && inertBefore.has(root)) inertBefore.set(root, false);
      syncLayers();
    };
    document.addEventListener('app-startup-ready', startupReady);
    const owns = () => layers.at(-1) === layer;
    const app = tg();
    // Never re-enable host swipe-to-close: it is unrelated to sheet dismissal.
    app?.disableVerticalSwipes?.();
    let closedHeight = Math.min(window.visualViewport?.height ?? innerHeight, app?.viewportHeight || innerHeight);
    let keyboard = false;
    let viewportWidth = innerWidth;
    const closedByWidth = new Map<number, number>([[viewportWidth, closedHeight]]);
    // Keep the existing layout wrapper; viewport geometry is not an animation.
    // The panel's transform remains exclusively owned by swipe dismissal.
    const motion = modal && element.parentElement?.hasAttribute('data-sheet-motion') ? element.parentElement : null;
    const host = modal ? motion?.parentElement ?? element.parentElement! : element;
    const viewport = sheetViewportMotion(host, element, () => app?.viewportHeight || 0, height => {
      if (Math.abs(innerWidth - viewportWidth) > 80) {
        const rotated = Math.abs(innerWidth - closedHeight) <= 80;
        closedHeight = keyboard && rotated ? closedByWidth.get(innerWidth) ?? viewportWidth : height;
      }
      viewportWidth = innerWidth;
      if (!editor(document.activeElement)) closedHeight = Math.max(closedHeight, height);
      keyboard = closedHeight - height > 100;
      if (!keyboard) closedByWidth.set(viewportWidth, closedHeight);
      if (keyboard && isKeyboardEditor(document.activeElement) && element.contains(document.activeElement)) {
        revealKeyboardEditor(document.activeElement, Math.min(height, host.getBoundingClientRect().bottom) - 12);
      }
    });
    const size = () => viewport.sample('browser');
    const hostSize = () => viewport.sample('host');
    const editorFocus = () => { if (keyboard && isKeyboardEditor(document.activeElement)) revealKeyboardEditor(document.activeElement); };
    element.addEventListener('focusin', editorFocus);
    request.current = reason => {
      if (!owns() || current.current.busy) return;
      const nested = element.querySelector<HTMLElement>('[role="listbox"]');
      if (nested) { nested.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true })); return; }
      const disclosure = element.querySelector<HTMLDetailsElement>('details[open]');
      if (disclosure) { disclosure.open = false; disclosure.querySelector('summary')?.focus(); return; }
      if (element.contains(document.activeElement) && editor(document.activeElement)) {
        (document.activeElement as HTMLElement).blur(); return;
      }
      if (keyboard) return; // Wait for actual resize after blur; no silent form loss.
      current.current.onClose(reason);
    };
    size(); window.addEventListener('resize', size);
    window.visualViewport?.addEventListener('resize', size); window.visualViewport?.addEventListener('scroll', size);
    app?.onEvent?.('viewportChanged', hostSize);
    const key = (event: KeyboardEvent) => {
      if (!owns() || event.defaultPrevented) return;
      if (event.key === 'Escape') {
        const disclosure = (document.activeElement as Element | null)?.closest('details[open]');
        if (disclosure instanceof HTMLDetailsElement && element.contains(disclosure)) { disclosure.open = false; disclosure.querySelector('summary')?.focus(); }
        else request.current('escape');
        event.preventDefault(); event.stopImmediatePropagation();
      }
      if (event.key === 'Tab' && modal) {
        const items = focusables(element), first = items[0], last = items.at(-1), active = document.activeElement;
        if (event.shiftKey && (active === first || !items.includes(active as HTMLElement))) { event.preventDefault(); (last ?? element).focus(); }
        else if (!event.shiftKey && (active === last || !items.includes(active as HTMLElement))) { event.preventDefault(); (first ?? element).focus(); }
      }
    };
    const focus = (event: FocusEvent) => { if (modal && owns() && !element.contains(event.target as Node)) element.focus({ preventScroll: true }); };
    document.addEventListener('keydown', key); document.addEventListener('focusin', focus);
    if (modal) element.focus({ preventScroll: true });
    type Drag = { x: number; y: number; dy: number; owner: 'pending' | 'sheet' };
    let drag: Drag | null = null, suppressUntil = 0;
    const reset = () => { drag = null; element.classList.remove('sheet-dragging'); element.style.removeProperty('transform'); };
    const start = (x: number, y: number, target: EventTarget | null) => {
      // A new deliberate input sequence is not the preceding drag's compatibility click.
      suppressUntil = 0;
      reset();
      if (!swipe || !owns() || current.current.busy || !(target instanceof Element) || editor(document.activeElement) || keyboard || !window.getSelection()?.isCollapsed) return;
      const header = target.closest('[data-sheet-header],[data-sheet-handle]');
      if (target.closest('input,select,textarea,[contenteditable],summary,a,button:not([data-sheet-handle]),[role="button"],[role="listbox"],[role="slider"]')) return;
      if (!header) {
        // Snapshot every containing scroller, including nested result lists.
        for (let node: Element | null = target; node && element.contains(node); node = node.parentElement) {
          if (node.scrollTop > 0) return;
        }
      }
      drag = { x, y, dy: 0, owner: 'pending' };
    };
    const move = (x: number, y: number, event: Event) => {
      if (!drag) return;
      if (!owns() || current.current.busy) { reset(); return; }
      const dx = x - drag.x, dy = y - drag.y;
      if (drag.owner === 'pending') {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 10) return;
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy) * .8) { reset(); return; }
        drag.owner = 'sheet'; element.classList.add('sheet-dragging');
      }
      if (!event.cancelable) { reset(); return; }
      event.preventDefault(); drag.dy = Math.max(0, dy); element.style.transform = `translateY(${drag.dy}px)`;
    };
    const end = (cancel = false) => {
      if (!drag) return;
      const dismiss = !cancel && drag.owner === 'sheet' && drag.dy >= Math.max(96, Math.min(150, element.clientHeight * .22));
      if (drag.owner === 'sheet') suppressUntil = performance.now() + 400;
      reset(); if (dismiss) request.current('swipe');
    };
    const touchstart = (e: TouchEvent) => { if (e.touches.length !== 1) { end(true); return; } start(e.touches[0]!.clientX, e.touches[0]!.clientY, e.target); };
    const touchmove = (e: TouchEvent) => { if (e.touches.length !== 1) { end(true); return; } move(e.touches[0]!.clientX, e.touches[0]!.clientY, e); };
    const touchend = () => end(), cancel = () => end(true);
    const click = (e: MouseEvent) => { if (performance.now() < suppressUntil) { e.preventDefault(); e.stopImmediatePropagation(); } };
    const pointerdown = (e: PointerEvent) => { if (e.pointerType === 'mouse' && e.button === 0) { start(e.clientX, e.clientY, e.target); if (drag) element.setPointerCapture(e.pointerId); } };
    const pointermove = (e: PointerEvent) => { if (e.pointerType === 'mouse') move(e.clientX, e.clientY, e); };
    const pointerup = (e: PointerEvent) => { if (e.pointerType === 'mouse') end(); };
    element.addEventListener('touchstart', touchstart, { passive: true }); element.addEventListener('touchmove', touchmove, { passive: false });
    element.addEventListener('touchend', touchend); element.addEventListener('touchcancel', cancel); element.addEventListener('click', click, true);
    element.addEventListener('pointerdown', pointerdown); element.addEventListener('pointermove', pointermove); element.addEventListener('pointerup', pointerup); element.addEventListener('pointercancel', cancel);
    return () => {
      document.removeEventListener('app-startup-ready', startupReady);
      element.removeEventListener('focusin', editorFocus);
      host.style.removeProperty('--sheet-height'); host.style.removeProperty('--sheet-top');
      reset(); document.removeEventListener('keydown', key); document.removeEventListener('focusin', focus);
      window.removeEventListener('resize', size); window.visualViewport?.removeEventListener('resize', size); window.visualViewport?.removeEventListener('scroll', size); app?.offEvent?.('viewportChanged', hostSize);
      viewport.dispose();
      element.removeEventListener('touchstart', touchstart); element.removeEventListener('touchmove', touchmove); element.removeEventListener('touchend', touchend); element.removeEventListener('touchcancel', cancel); element.removeEventListener('click', click, true);
      element.removeEventListener('pointerdown', pointerdown); element.removeEventListener('pointermove', pointermove); element.removeEventListener('pointerup', pointerup); element.removeEventListener('pointercancel', cancel);
      const wasTop = owns(); layers.splice(layers.indexOf(layer), 1); syncLayers();
      if (modal && wasTop) {
        if (prior?.isConnected && prior !== document.body && !prior.closest('[inert]')) prior.focus({ preventScroll: true });
        else queueMicrotask(() => current.current.returnFocus?.());
      }
    };
  }, [modal, swipe]);
  return { panel, requestDismiss: (reason: DismissReason) => request.current(reason) };
}
